import React, { useState } from 'react';
import { Routes, useLocation } from 'react-router-dom';

/*
 * Fresh navigation: the sidebar, the mobile tabs, the breadcrumbs and the command palette open a
 * page from its start, not from the last view the user left it in.
 *
 * - Every shell link navigates with state { fresh: <nonce> }.
 * - <FreshRoutes> remounts the route tree when a new nonce arrives, so the page's own state
 *   (open tool, sub-view, scroll, filters held in memory) starts over. Normal in-page navigation
 *   (search params, tabs) has no nonce and keeps the page mounted.
 * - VIEW_STATE_KEYS are "where was I" values a page restores from storage; they are cleared on a
 *   fresh open. Drafts and saved data (form drafts, column choices, caches) are NOT listed here.
 */
export const VIEW_STATE_KEYS = ['yelo_ops_active_tool'];

export function clearViewState() {
    VIEW_STATE_KEYS.forEach((k) => {
        try { localStorage.removeItem(k); } catch (e) { /* storage unavailable */ }
    });
}

/** Router state for a fresh open (a new nonce on every click, even on the same page). */
export const freshState = () => ({ fresh: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` });

/** True when a click should be left to the browser (new tab / window, middle click …). */
export const isModifiedClick = (e) => e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

/** Navigate to `path` from its start. */
export function openFresh(navigate, path) {
    clearViewState();
    navigate(path, { state: freshState() });
}

/** Click handler for a <Link>/<NavLink>: keeps the real href (open in new tab still works). */
export const freshClick = (navigate, path, after) => (e) => {
    if (isModifiedClick(e)) return;
    e.preventDefault();
    openFresh(navigate, path);
    if (after) after(e);
};

/** <Routes> that remounts its page when a fresh navigation arrives. */
export function FreshRoutes({ children }) {
    const location = useLocation();
    const fresh = (location.state && location.state.fresh) || null;
    const [seen, setSeen] = useState(fresh);
    const [generation, setGeneration] = useState(0);
    if (fresh && fresh !== seen) {
        setSeen(fresh);
        setGeneration((g) => g + 1);
    }
    return <Routes key={generation}>{children}</Routes>;
}
